// src/pages/Marketplace/surge-pricing/CreateEventModal.tsx
import React, { useEffect, useState } from 'react';
import { ErrorBanner } from '@/pages/Admin/shared';
import {
  BASE_PRICE_CENTS,
  useSurgeEventMutations,
  utcIsoToZonedInput,
  zonedInputToUtcIso,
} from '@/pages/Marketplace/shared';
import type { Market, SurgeEvent } from '@/pages/Marketplace/shared';

interface Props {
  market: Market;
  editing: SurgeEvent | null;
  apiUrl: string;
  accessToken: string | null;
  onClose: () => void;
  onSaved: () => void;
}

interface Draft {
  name: string;
  multiplier: string;
  starts_at: string;   // datetime-local string in market tz
  ends_at: string;
  notes: string;
}

export default function CreateEventModal({
  market, editing, apiUrl, accessToken, onClose, onSaved,
}: Props): React.ReactElement {
  const [draft, setDraft] = useState<Draft>({
    name: '', multiplier: '2.0', starts_at: '', ends_at: '', notes: '',
  });
  const mutations = useSurgeEventMutations(apiUrl, accessToken);

  useEffect(() => {
    if (editing) {
      setDraft({
        name: editing.name,
        multiplier: String(editing.multiplier),
        starts_at: utcIsoToZonedInput(editing.starts_at, market.timezone),
        ends_at: utcIsoToZonedInput(editing.ends_at, market.timezone),
        notes: editing.notes ?? '',
      });
    }
  }, [editing, market.timezone]);

  const multiplier = parseFloat(draft.multiplier);
  const invalid =
    !draft.name.trim() ||
    Number.isNaN(multiplier) || multiplier < 0.1 || multiplier > 10 ||
    !draft.starts_at || !draft.ends_at ||
    new Date(draft.starts_at) >= new Date(draft.ends_at);

  const previewCents = !Number.isNaN(multiplier) ? Math.round(BASE_PRICE_CENTS * multiplier) : null;

  const handleSave = async () => {
    const body = {
      market_id: market.id,
      name: draft.name.trim(),
      multiplier,
      starts_at: zonedInputToUtcIso(draft.starts_at, market.timezone),
      ends_at: zonedInputToUtcIso(draft.ends_at, market.timezone),
      notes: draft.notes.trim() || null,
    };
    const result = editing ? await mutations.update(editing.id, body) : await mutations.create(body);
    if (result) { onSaved(); onClose(); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-xl w-full max-w-lg">
        <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between">
          <h3 className="text-white font-semibold">{editing ? 'Edit' : 'Create'} event</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">✕</button>
        </div>
        <div className="p-6 space-y-3">
          <label className="block">
            <span className="text-xs text-gray-400">Name</span>
            <input
              className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
              value={draft.name}
              onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            />
          </label>
          <label className="block">
            <span className="text-xs text-gray-400">Multiplier (0.1–10.0)</span>
            <input
              type="number" step="0.1" min="0.1" max="10"
              className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
              value={draft.multiplier}
              onChange={(e) => setDraft((d) => ({ ...d, multiplier: e.target.value }))}
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-gray-400">Starts</span>
              <input
                type="datetime-local"
                className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
                value={draft.starts_at}
                onChange={(e) => setDraft((d) => ({ ...d, starts_at: e.target.value }))}
              />
              <span className="text-[10px] text-gray-500">Entering time in {market.timezone}</span>
            </label>
            <label className="block">
              <span className="text-xs text-gray-400">Ends</span>
              <input
                type="datetime-local"
                className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
                value={draft.ends_at}
                onChange={(e) => setDraft((d) => ({ ...d, ends_at: e.target.value }))}
              />
              <span className="text-[10px] text-gray-500">Entering time in {market.timezone}</span>
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-gray-400">Internal notes</span>
            <textarea
              rows={2}
              className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm mt-1"
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            />
          </label>

          {previewCents !== null && (
            <div className="text-xs text-gray-400">
              Preview: ${(BASE_PRICE_CENTS / 100).toFixed(2)} × {multiplier} ={' '}
              <span className="text-violet-300">${(previewCents / 100).toFixed(2)}</span> at peak.
            </div>
          )}

          {mutations.error && <ErrorBanner message={mutations.error} />}
        </div>
        <div className="px-6 py-4 border-t border-gray-800 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-sm text-gray-300 hover:text-white">Cancel</button>
          <button
            onClick={handleSave}
            disabled={invalid || mutations.loading}
            className="px-4 py-2 text-sm font-medium bg-violet-600 hover:bg-violet-700 disabled:opacity-40 rounded"
          >
            {mutations.loading ? 'Saving…' : (editing ? 'Save changes' : 'Create event')}
          </button>
        </div>
      </div>
    </div>
  );
}
